import { trpc } from './trpc';
import { inferRouterInputs, inferRouterOutputs } from '@trpc/server';
import { AppRouter } from '@tagadapay/trpc-types';
import { useToast } from '@/components/ui/use-toast';
import { useTranslation } from 'react-i18next';

type OutputRouter = inferRouterOutputs<AppRouter>
type InputRouter = inferRouterInputs<AppRouter>
export type SubscriptionListOutput = OutputRouter['subscriptions']['list']
export type Subscription = SubscriptionListOutput['subscriptions'][number]
export type SubscriptionDetail = OutputRouter['subscriptions']['details']
export type BulkCancelSubscriptionInput = InputRouter['subscriptions']['bulkCancel']
export type ResumeSubscriptionInput = InputRouter['subscriptions']['resume']
export type PauseSubscriptionInput = InputRouter['subscriptions']['pause']
export type BulkItemOutcome = OutputRouter['subscriptions']['bulkPause'][number]

const summarizeBulkResults = (results: readonly BulkItemOutcome[]) => {
    const notSucceeded = results.filter((r) => r.status !== 'success');
    return {
        total: results.length,
        succeeded: results.length - notSucceeded.length,
        failed: results.filter((r) => r.status === 'failed').length,
        skipped: results.filter((r) => r.status === 'skipped').length,
        reasons: Array.from(
            new Set(notSucceeded.map((r) => r.skipReason ?? r.error).filter((r): r is string => Boolean(r))),
        ),
    };
};

// Every bulk mutation now returns one result per subscription. Reporting a flat success would
// hide the per-item failures that made TAG-2199 invisible for ten days.
function useBulkResultToast() {
    const { toast } = useToast()
    const { t } = useTranslation('subscriptions')
    return (results: readonly BulkItemOutcome[]) => {
        const { total, succeeded, failed, skipped, reasons } = summarizeBulkResults(results);
        if (succeeded === total) {
            toast({
                title: t('bulkActions.result.successTitle'),
                description: t('bulkActions.result.success', { count: succeeded }),
            })
            return;
        }
        const summary = succeeded === 0
            ? t('bulkActions.result.none', { total })
            : t('bulkActions.result.partial', { succeeded, total, failed, skipped });
        const detail = reasons.length
            ? ` ${t('bulkActions.result.reasons', {
                reasons: reasons.map((r) => t(`bulkActions.result.reason.${r}`, { defaultValue: r })).join(', '),
            })}`
            : '';
        toast({
            title: t('bulkActions.result.incompleteTitle'),
            description: `${summary}${detail}`,
            variant: 'destructive',
        })
    };
}

export function useSubscriptionsList(
    params?: Parameters<typeof trpc.subscriptions.list.useQuery>[0]
) {
    const queryParams = params || {
        sortBy: { field: 'subscriptions.createdAt', direction: 'desc' },
        pagination: { page: 1, pageSize: 10 },
        filters: {},
    };
    return trpc.subscriptions.list.useQuery(queryParams);
}

export function useSubscriptionDetails(id: string) {
    return trpc.subscriptions.details.useQuery({ id });
}

export function useBulkUpdateBillingCycle() {
    const { toast } = useToast()
    const reportBulkResult = useBulkResultToast()
    const utils = trpc.useUtils();
    return trpc.subscriptions.bulkUpdateBillingCycle.useMutation({
        onSuccess: (results) => {
            utils.subscriptions.list.invalidate();
            utils.subscriptions.details.invalidate();
            reportBulkResult(results);
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message,
                variant: 'destructive',
            })
        }
    });
}

export function useBulkCancelSubscription() {
    const { toast } = useToast()
    const { t } = useTranslation('subscriptions')
    const utils = trpc.useUtils();
    return trpc.subscriptions.bulkCancel.useMutation({
        onSuccess: ({ requested, canceled }) => {
            utils.subscriptions.list.invalidate();
            utils.subscriptions.details.invalidate();
            if (canceled < requested) {
                toast({
                    title: t('bulkActions.cancel.result.incompleteTitle'),
                    description: canceled === 0
                        ? t('bulkActions.cancel.result.none', { count: requested })
                        : t('bulkActions.cancel.result.partial', { count: canceled, requested }),
                    variant: 'destructive',
                })
                return
            }
            toast({
                title: t('bulkActions.cancel.result.successTitle'),
                description: t('bulkActions.cancel.result.success', { count: canceled }),
            })
        },
        onError: (error) => {
            console.error('[CLIENT useBulkCancelSubscription] Error:', error);
            toast({
                title: 'Error',
                description: error.message,
                variant: 'destructive',
            })
        }
    });
}

export function useResumeSubscription() {
    const { toast } = useToast()
    const utils = trpc.useUtils();
    return trpc.subscriptions.resume.useMutation({
        onSuccess: () => {
            utils.subscriptions.list.invalidate();
            utils.subscriptions.details.invalidate();
            toast({
                title: 'Success',
                description: 'Subscription resumed successfully',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message,
                variant: 'destructive',
            })
        }
    });
}

export function usePauseSubscription() {
    const { toast } = useToast()
    const utils = trpc.useUtils();
    return trpc.subscriptions.pause.useMutation({
        onSuccess: () => {
            utils.subscriptions.list.invalidate();
            utils.subscriptions.details.invalidate();
            toast({
                title: 'Success',
                description: 'Subscription paused successfully',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message,
                variant: 'destructive',
            })
        }
    });
}

export function useRebillSubscription() {
    const { toast } = useToast()
    const { t } = useTranslation('subscriptions')
    const utils = trpc.useUtils();
    return trpc.subscriptions.rebill.useMutation({
        onSuccess: (data) => {
            utils.subscriptions.list.invalidate();
            utils.subscriptions.details.invalidate();
            const outcome = data?.outcome;
            if (!outcome || outcome.status === 'captured') {
                toast({
                    title: t('rebill.result.capturedTitle'),
                    description: t('rebill.result.captured'),
                })
                return;
            }
            if (outcome.status === 'pending') {
                toast({
                    title: t('rebill.result.pendingTitle'),
                    description: t(`rebill.result.pending.${outcome.reason}`),
                })
                return;
            }
            // Anything else means the customer was not charged. A platform-class decline is our
            // fault, not the cardholder's — say so rather than reporting a card problem.
            toast({
                title: t(`rebill.result.${outcome.status}Title`),
                description:
                    outcome.status === 'declined' && outcome.failureClass === 'platform'
                        ? t('rebill.result.platformFailure', { reason: outcome.reason })
                        : t(`rebill.result.${outcome.status}`, { reason: outcome.reason }),
                variant: 'destructive',
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message,
                variant: 'destructive',
            })
        }
    });
}

export function useChangeBulkSubscriptionProcessor() {
    const { toast } = useToast()
    const reportBulkResult = useBulkResultToast()
    const utils = trpc.useUtils();
    return trpc.subscriptions.bulkChangeProcessor.useMutation({
        onSuccess: (results) => {
            utils.subscriptions.list.invalidate();
            utils.subscriptions.details.invalidate();
            reportBulkResult(results);
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message,
                variant: 'destructive',
            })
        }
    });
}

export const useBulkPauseSubscription = () => {
    const { toast } = useToast()
    const reportBulkResult = useBulkResultToast()
    const utils = trpc.useUtils();
    return trpc.subscriptions.bulkPause.useMutation({
        onSuccess: (results) => {
            utils.subscriptions.list.invalidate();
            utils.subscriptions.details.invalidate();
            reportBulkResult(results);
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message,
                variant: 'destructive',
            })
        }
    });
}

export const bulkResumeSubscriptions = () => {
    const { toast } = useToast()
    const reportBulkResult = useBulkResultToast()
    const utils = trpc.useUtils();
    return trpc.subscriptions.bulkResumeSubscriptions.useMutation({
        onSuccess: (results) => {
            utils.subscriptions.list.invalidate();
            utils.subscriptions.details.invalidate();
            reportBulkResult(results);
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message,
                variant: 'destructive',
            })
        }
    });
}

export const useBulkEditSubscriptionPrice = () => {
    const { toast } = useToast()
    const reportBulkResult = useBulkResultToast()
    const utils = trpc.useUtils();
    return trpc.subscriptions.bulkEditSubscriptionPrice.useMutation({
        onSuccess: (results) => {
            utils.subscriptions.list.invalidate();
            utils.subscriptions.details.invalidate();
            reportBulkResult(results);
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message,
                variant: 'destructive',
            })
        }
    });
}

export const useBulkUpdateShippingRate = () => {
    const { toast } = useToast()
    const reportBulkResult = useBulkResultToast()
    const utils = trpc.useUtils();
    return trpc.subscriptions.bulkUpdateShippingRate.useMutation({
        onSuccess: (results) => {
            utils.subscriptions.list.invalidate();
            utils.subscriptions.details.invalidate();
            reportBulkResult(results);
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message,
                variant: 'destructive',
            })
        }
    });
}

export const useBulkUpdateQuantity = () => {
    const { toast } = useToast()
    const reportBulkResult = useBulkResultToast()
    const utils = trpc.useUtils();
    return trpc.subscriptions.bulkUpdateQuantity.useMutation({
        onSuccess: (results) => {
            utils.subscriptions.list.invalidate();
            utils.subscriptions.details.invalidate();
            reportBulkResult(results);
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message,
                variant: 'destructive',
            })
        }
    });
}

export const useBulkSetTagadaVault = () => {
    const { toast } = useToast()
    const reportBulkResult = useBulkResultToast()
    const utils = trpc.useUtils();
    return trpc.subscriptions.bulkSetTagadaVault.useMutation({
        onSuccess: (results) => {
            utils.subscriptions.list.invalidate();
            utils.subscriptions.details.invalidate();
            reportBulkResult(results);
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message,
                variant: 'destructive',
            })
        }
    });
}

export type PreviewCreateFromOrdersInput = InputRouter['subscriptions']['previewCreateFromOrders']
export type PreviewCreateFromOrdersOutput = OutputRouter['subscriptions']['previewCreateFromOrders']
export type ResolvedOrderSubscription = PreviewCreateFromOrdersOutput['accepted'][number]
export type RejectedOrderSubscription = PreviewCreateFromOrdersOutput['rejected'][number]
export type CreateFromOrdersOutput = OutputRouter['subscriptions']['createFromOrders']

export function usePreviewCreateSubscriptionsFromOrders(
    input: PreviewCreateFromOrdersInput,
    enabled: boolean,
) {
    return trpc.subscriptions.previewCreateFromOrders.useQuery(input, { enabled });
}

export const useCreateSubscriptionsFromOrders = () => {
    const { toast } = useToast()
    const { t } = useTranslation('subscriptions')
    const utils = trpc.useUtils();
    return trpc.subscriptions.createFromOrders.useMutation({
        onSuccess: (results) => {
            utils.subscriptions.list.invalidate();
            utils.orders.invalidate();
            const created = results.filter((r) => r.status === 'created').length;
            toast({
                title: t('createFromOrders.toast.title'),
                description: t('createFromOrders.toast.description', {
                    count: created,
                    total: results.length,
                }),
            })
        },
        onError: (error) => {
            toast({
                title: 'Error',
                description: error.message,
                variant: 'destructive',
            })
        }
    });
}
